import React, { ReactNode, useRef } from "react";
import { Drawer, useMediaQuery } from "@mui/material";
import { useSelector } from "react-redux";

import {
  Container,
  Header,
  HeaderText,
  IconContainer,
  BoxItem,
  ButtonContainer,
  ContentBox,
  LoadingContainer,
  LoadingText,
} from "./standardModal.styles";

import { selectOnlineOrdersConfigs } from "../../../store/kitchen/kitchen.selector";
import { getColor } from "../../../utils/utils.common";
import { CloseIcon } from "../../icons";
import Button from "../../button/button.component";
import Spinner from "../../spinner/spinner.component";
import ErrorMessage from "../errorMessage/errorMessage.component";

interface StandardModalProps {
  isOpen: boolean;
  toggleBottomSheet: () => void;
  title?: string;
  children?: ReactNode;
  showFooter?: boolean;
  onConfirm?: () => void;
  isConfirmDisabled?: boolean;
  confirmLabel?: string;
  loading?: boolean;
  loadingContent?: string;
  errorMessage?: string;
}

const StandardModal = ({
  isOpen,
  toggleBottomSheet,
  title = "Modal Title",
  children,
  showFooter = true,
  onConfirm,
  isConfirmDisabled = false,
  confirmLabel = "Confirm",
  loading = false,
  loadingContent = "",
  errorMessage,
}: StandardModalProps) => {
  const onlineOrdersConfig = useSelector(selectOnlineOrdersConfigs);
  const isMobile = useMediaQuery("(max-width: 768px)");
  const contentRef = useRef(null);

  const brandTertiary = getColor("brand-tertiary", onlineOrdersConfig);
  const brandAccent = getColor("brand-accent", onlineOrdersConfig);
  const brandPrimary = getColor("brand-primary", onlineOrdersConfig);
  const textBody = getColor("text-body", onlineOrdersConfig);

  const headerModalBackground = getColor(
    "header-modal-background",
    onlineOrdersConfig
  );
  const backgroundSecondary = getColor(
    "background-secondary",
    onlineOrdersConfig
  );

  return (
    <Drawer
      transitionDuration={{
        enter: isMobile ? 300 : 0,
        exit: isMobile ? 300 : 0,
      }}
      open={isOpen}
      onClose={toggleBottomSheet}
      anchor={isMobile ? "bottom" : undefined}
      sx={{
        "& .MuiDrawer-paper": {
          background: headerModalBackground,
          position: !isMobile && "absolute",
          top: !isMobile && "50%",
          left: !isMobile && "50%",
          transform: !isMobile && "translate(-50%, -50%) !important",
          width: isMobile ? "100%" : "560px",
          height: "auto",
          minHeight: "270px",
          maxHeight: "90vh",
          boxSizing: "border-box",
          borderRadius: !isMobile && "8px",
          borderTopLeftRadius: isMobile && "8px",
          borderTopRightRadius: isMobile && "8px",
          overflow: "hidden",
          display: "block",
        },
      }}
    >
      <Container ref={contentRef}>
        <ContentBox>
          <Header headerModalBackground={headerModalBackground}>
            <HeaderText brandAccent={brandAccent}>{title}</HeaderText>
            <IconContainer
              brandTertiary={brandTertiary}
              headerModalBackground={headerModalBackground}
              onClick={toggleBottomSheet}
            >
              <CloseIcon size={isMobile ? "middle" : "large"} />
            </IconContainer>
          </Header>
          {loading ? (
            <LoadingContainer>
              <Spinner size={64} color={brandPrimary} />
              <LoadingText textBody={textBody}>{loadingContent}</LoadingText>
            </LoadingContainer>
          ) : (
            <BoxItem>{children}</BoxItem>
          )}
        </ContentBox>
        {errorMessage && <ErrorMessage errorMessage={errorMessage} />}
        {showFooter && !loading && (
          <ButtonContainer
            headerModalBackground={headerModalBackground}
            backgroundSecondary={backgroundSecondary}
          >
            <Button disabled={isConfirmDisabled} onClick={onConfirm}>
              {confirmLabel}
            </Button>
          </ButtonContainer>
        )}
      </Container>
    </Drawer>
  );
};

export default StandardModal;
